import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import type { ApiException } from '@/lib/api/types';
import { formatUsdPrice } from '@/lib/format';
import type { IndicatorHelp } from './indicatorHelp';
import type { MarketScope } from './marketScope';

/**
 * 시장지표 탭의 비트코인 · 금 · 국고채 · 지수 (2026-09-29 I-81 · 사용자 결정 "환율 탭을 시장지표로 · 하위 탭").
 *
 * 서버 GET /api/trading/market-series/{code}(MarketSeriesController) — DB 만 읽는다. 외부 호출은 매일 09:20 배치가 한다.
 * 값은 하루 한 점(종가). KRX 는 다음 날 아침에, 비트코인은 09:00 에 하루가 끝난다.
 * ✏️ 2026-10-17 — 국고채 · 지수 하위 탭은 위 국내 / 미국 스위치를 따른다(사용자 Q17 · US_TABS). 미국 국채는 미국 재무부 · 다음 날 아침
 *   미국 지수 탭은 SPY · QQQ ETF 값 — 서버가 토스 미국 일봉(매일 10:30 수집)을 읽어 같은 모양으로 준다
 */

export type MarketSeriesCode =
  | 'GOLD' | 'KTB3Y' | 'KTB10Y' | 'KOSPI' | 'KOSDAQ' | 'BTC' | 'UST2Y' | 'UST10Y' | 'SPY' | 'QQQ';

/** 백엔드 MarketSeriesResponse 와 1:1 */
export interface MarketSeries {
  code: MarketSeriesCode;
  label: string;
  /** 원 · % · pt · 달러 */
  unit: string;
  source: 'KRX' | 'UPBIT' | 'TREASURY' | 'TOSS';
  /** 화면에 그대로 적는 출처 문구 — KRX 는 약관상 "한국거래소 통계정보" 를 밝혀야 한다 */
  sourceNote: string;
  points: { date: string; value: number }[];
}

/** 하위 탭 — 순서는 사용자 결정(환율 · 비트코인 · 금 · 국고채 · 지수) · 기본 환율 */
export const MARKET_TABS = [
  { key: 'fx', label: '환율', codes: [] as MarketSeriesCode[] },
  { key: 'btc', label: '비트코인', codes: ['BTC'] as MarketSeriesCode[] },
  { key: 'gold', label: '금', codes: ['GOLD'] as MarketSeriesCode[] },
  { key: 'bond', label: '국고채', codes: ['KTB3Y', 'KTB10Y'] as MarketSeriesCode[] },
  { key: 'index', label: '지수', codes: ['KOSPI', 'KOSDAQ'] as MarketSeriesCode[] },
] as const;

export type MarketTabKey = (typeof MARKET_TABS)[number]['key'];

/** 주소의 ?tab= 값을 하위 탭으로 — 모르는 값 · 없음은 환율(기본) */
export function marketTabOf(raw: string | null): MarketTabKey {
  return MARKET_TABS.find((t) => t.key === raw)?.key ?? 'fx';
}

/**
 * 미국 모드에서 내용이 바뀌는 하위 탭 (2026-10-17 사용자 Q17 "시장지표가 국내 / 미국 스위치를 따른다").
 * 여기 없는 탭(환율 · 비트코인 · 금)은 두 시장이 같다 — 위 MARKET_TABS 의 codes 가 국내 · 미국 공통이다.
 *   국고채 → 미국 국채 2년 · 10년(미국 재무부 · 백엔드 MarketSeriesCode UST2Y · UST10Y) · 탭 이름도 "미국 국채"
 *   지수   → S&P 500 · 나스닥 100 을 따르는 ETF(SPY · QQQ) 종가 — 사용자 결정 10/17 18:3x(137번 7.3 권고 ②).
 *            🔴 지수 포인트가 아니라 ETF 값(달러)이다 · QQQ 는 나스닥 종합이 아니다 — 카드 이름 · 물음표 · 출처 문구가 그렇게 말한다
 */
const US_TABS: Partial<Record<MarketTabKey, { label: string; codes: readonly MarketSeriesCode[] }>> = {
  bond: { label: '미국 국채', codes: ['UST2Y', 'UST10Y'] },
  index: { label: '지수', codes: ['SPY', 'QQQ'] },
};

/** 이 하위 탭이 지금 시장에서 보여 줄 계열 — 미국이면 US_TABS 가 있으면 그것, 없으면 공통 */
export function seriesCodesOf(tab: MarketTabKey, scope: MarketScope): readonly MarketSeriesCode[] {
  const us = scope === 'US' ? US_TABS[tab] : undefined;
  return us ? us.codes : (MARKET_TABS.find((t) => t.key === tab)?.codes ?? []);
}

/** 하위 탭 이름 — 국고채는 미국이면 "미국 국채"(국고채는 한국 정부 채권의 이름이다) */
export function marketTabLabel(tab: MarketTabKey, scope: MarketScope): string {
  const us = scope === 'US' ? US_TABS[tab] : undefined;
  return us ? us.label : (MARKET_TABS.find((t) => t.key === tab)?.label ?? '');
}

/**
 * 주소의 ?tab= 이 국내 · 미국 공통 하위 탭인가 — 스위치 옆 "이 화면은 국내 · 미국 공통입니다" 문구를 켤지(TradingNav).
 * 🔴 국고채 · 지수에서 그 문구가 뜨면 거짓말이 된다(전환하면 내용이 바뀐다)
 */
export function isCommonMarketTab(raw: string | null): boolean {
  return US_TABS[marketTabOf(raw)] === undefined;
}

/**
 * 수집 시각 — 화면 문구용. 정본은 MarketSeriesSyncJob 의 @Scheduled(cron = "0 20 9 * * *").
 * 🔴 cron 을 바꾸면 여기도 바꾼다(CLAUDE.md 6절 9항) — 빈 카드 안내 · 국채 도움말이 이 값을 쓴다
 */
export const COLLECT_TIME_LABEL = '오전 9시 20분';

/**
 * 국채 탭 물음표 (2026-10-17) — 국내 / 미국 스위치에 따라 무엇이 보이는지 · 출처 · 언제 들어오는지.
 * 만기(국내 3년 · 10년 · 미국 2년 · 10년)와 출처는 백엔드 MarketSeriesCode · 시각은 COLLECT_TIME_LABEL 과 맞춘다
 */
export const BOND_HELP: IndicatorHelp = {
  label: '국채 금리 — 국내 / 미국',
  hint: '스위치에 따라 국고채 또는 미국 국채',
  detail: [
    '이 탭은 위 국내 / 미국 스위치를 따릅니다. 국내주식이면 국고채 3년 · 10년, 미국주식이면 미국 국채 2년 · 10년을 보여 줍니다. 환율 · 비트코인 · 금 탭은 두 시장이 같습니다.',
    '국고채 = 한국거래소(KRX) 국채 시장의 대표("지표") 종목이 그날 마지막으로 거래된 수익률입니다. 한국거래소가 다음 날 공개해 전 거래일 값이 보입니다.',
    '미국 국채 = 미국 재무부가 영업일마다 내는 국채 수익률표(Daily Treasury Par Yield Curve)의 2년 · 10년 칸입니다. 뉴욕 오후 3시 30분 무렵 값이고 날짜는 미국 날짜입니다. 미국 채권시장이 쉬는 날(콜럼버스 데이 · 재향군인의 날 등 주식시장은 여는 날 포함)은 값이 없습니다.',
    `둘 다 매일 ${COLLECT_TIME_LABEL}에 모읍니다. 변화는 %p(퍼센트포인트)로 적습니다 — 4.80%가 4.90%가 되면 +0.100%p 입니다.`,
  ],
};

/**
 * 미국 일봉 수집 시각 — 화면 문구용. 정본은 DailyCandleSyncJob.runUs 의 @Scheduled(cron = "0 30 10 * * *").
 * 🔴 cron 을 바꾸면 여기와 백엔드 MarketSeriesController.sourceNote(TOSS) 를 함께 바꾼다
 */
export const US_CANDLE_TIME_LABEL = '오전 10시 30분';

/**
 * 미국 모드 지수 탭 물음표 (2026-10-17 · 사용자 결정 "이름은 정직하게") — 지수가 아니라 지수를 따르는 ETF 값이라는 것.
 * 국내 모드 지수 탭(코스피 · 코스닥)에는 붙이지 않는다(사용자 — "국내 모드는 지금 그대로")
 */
export const US_INDEX_HELP: IndicatorHelp = {
  label: 'S&P 500 · 나스닥 100 — ETF 값',
  hint: '지수 포인트가 아니라 지수를 따르는 ETF 값',
  detail: [
    '이 카드는 지수 포인트가 아니라 지수를 따르는 ETF(상장지수펀드)의 하루 마지막 값(달러)입니다. SPY 는 S&P 500 을, QQQ 는 나스닥 100 을 따릅니다. 오르내린 비율은 지수와 거의 같지만 값의 크기는 지수와 다릅니다.',
    'QQQ 는 나스닥 종합지수가 아니라 나스닥 100(나스닥에 상장된 큰 회사 100곳)을 따릅니다. 뉴스에서 말하는 "나스닥 지수" 와 오르내림이 조금 다를 수 있습니다.',
    'ETF 는 운용 보수가 매일 조금씩 빠지고, 배당을 나눠 준 날 그만큼 값이 내려가 지수와 조금 다르게 움직입니다.',
    `출처는 토스증권 미국 일봉입니다. 매일 ${US_CANDLE_TIME_LABEL}에 전날 밤 끝난 미국 장의 값을 모으고, 날짜는 미국 날짜입니다.`,
  ],
};

/** 추이 기간(달력일) — 환율 탭과 같은 버튼(3개월 · 6개월 · 1년 · 3년) */
export const SERIES_RANGES = [
  { days: 92, label: '3개월' },
  { days: 183, label: '6개월' },
  { days: 366, label: '1년' },
  { days: 1100, label: '3년' },
] as const;

/** 한 번에 3년치를 받고 기간은 화면에서 자른다 — 버튼마다 다시 부르지 않는다 */
const FETCH_DAYS = 1100;

export function useMarketSeries(code: MarketSeriesCode) {
  return useQuery<MarketSeries, ApiException>({
    queryKey: ['trading', 'market-series', code],
    queryFn: () => api.get<MarketSeries>(`/trading/market-series/${code}`, { days: FETCH_DAYS }),
    // 하루 한 번 바뀌는 값이다 — 30분이면 충분하다
    staleTime: 30 * 60 * 1000,
  });
}

/** 기준일(today)에서 days 전 이후의 점만 */
export function pointsSince(points: MarketSeries['points'], days: number, today: Date = new Date()): MarketSeries['points'] {
  const from = new Date(today.getTime() - days * 86400000).toISOString().slice(0, 10);
  return points.filter((p) => p.date >= from);
}

/**
 * 값 표기 — 단위마다 자릿수가 다르다.
 *   원  189,500원 · 114,100,000원(정수)
 *   %   4.004%(국고채 금리 · 셋째 자리까지 — KRX 가 주는 자릿수)
 *   pt  7,080.92(지수 · 둘째 자리)
 *   달러 $705.10(SPY · QQQ ETF 값 · 2026-10-17 — 미국 주가 표기 lib/format formatUsdPrice 를 쓴다)
 */
export function formatSeriesValue(value: number, unit: string): string {
  if (unit === '%') {
    return `${value.toFixed(3)}%`;
  }
  if (unit === 'pt') {
    return value.toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (unit === '달러') {
    return formatUsdPrice(value);
  }
  return `${Math.round(value).toLocaleString('ko-KR')}원`;
}

/**
 * 변화 표기 — 마지막 점과 비교 기준 점 사이.
 *   금리(%)는 %p 로만 말한다 — "4.0% 가 1% 올랐다" 를 비율(%)로 적으면 뜻이 둘이 된다
 *   그 밖은 "+380원 (+0.20%)" · "+63.21 (+0.90%)"
 * 기준 점이 없거나 0 이면 null
 */
export function describeSeriesChange(last: number, base: number | null, unit: string): { text: string; sign: number } | null {
  if (base === null || base === 0) {
    return null;
  }
  const diff = last - base;
  const sign = diff > 0 ? 1 : diff < 0 ? -1 : 0;
  const plus = diff > 0 ? '+' : '';
  if (unit === '%') {
    return { text: `${plus}${diff.toFixed(3)}%p`, sign };
  }
  const rate = (diff / base) * 100;
  // 달러는 formatUsdPrice 가 음수 부호를 붙인다("-$7.24") — 양수에만 + 를 앞에 붙이는 것은 다른 단위와 같다
  const amount = unit === 'pt'
    ? diff.toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : unit === '달러'
      ? formatUsdPrice(diff)
      : `${Math.round(diff).toLocaleString('ko-KR')}원`;
  return { text: `${plus}${amount} (${plus}${rate.toFixed(2)}%)`, sign };
}
